import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Paper,
} from '@mui/material';
import { Add as AddIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { useAuth } from '../../../contexts/AuthContext';
import useUserNotes from '../hooks/useUserNotes';
import NoteItem from './NoteItem';
import AddNoteForm from './AddNoteForm';

interface UserNotesProps {
  userId: string;
}

const UserNotes: React.FC<UserNotesProps> = ({ userId }) => {
  const { user } = useAuth();
  const [showAddForm, setShowAddForm] = useState<null | { mode: 'note' | 'task' }>(null);
  
  const {
    notes,
    isLoading,
    error,
    createNote,
    updateNote,
    deleteNote,
    refresh,
  } = useUserNotes(user?.token || null, userId);

  const handleCreateNote = async (text: string) => {
    if (!showAddForm) return;

    const isTask = showAddForm.mode === 'task';
    await createNote(text, isTask, false);
    setShowAddForm(null);
  };


  if (isLoading && notes.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Notes</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={refresh}
            disabled={isLoading}
          >
            Refresh
          </Button>
          {!showAddForm && (
            <>
              <Button
                size="small"
                variant="contained"
                onClick={() => setShowAddForm({ mode: 'note' })}
              >
                Add Note
              </Button>

              <Button
                size="small"
                variant="contained"
                color="secondary"
                onClick={() => setShowAddForm({ mode: 'task' })}
              >
                Add Task
              </Button>
            </>
          )}

        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {showAddForm && (
        <AddNoteForm
          onSubmit={handleCreateNote}
          onCancel={() => setShowAddForm(null)}
          isTask={showAddForm.mode === 'task'}
        />
      )}


      {notes.length === 0 && !showAddForm ? (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            textAlign: 'center',
            backgroundColor: 'grey.50',
            border: '1px dashed',
            borderColor: 'grey.300',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            No notes yet. Click "Add Note" to create one.
          </Typography>
        </Paper>
      ) : (
        <Box sx={{ mt: 2 }}>
          {notes.map((note) => (
            <NoteItem
              key={note.id}
              note={note}
              onUpdate={updateNote}
              onDelete={deleteNote}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default UserNotes;